import React, { useMemo } from 'react';

import classNames from 'classnames';

import { formatFigure, formatSeparateByThousands, formatToFixedLocaleString, formatToFixedMoneyWithLocale } from 'Helpers/formatService';

import TableSortHeaderCell from 'CommonComponents/TableSortHeaderCell';

import useSort, { SORT_DIR_ASC, SORT_DIR_DESC } from 'Hooks/useSort';

/**
 * Savings from delayed hires, by level.
 *
 * Realised sits beside modelled deliberately: one is already true, the other is a
 * choice. Both come from the same engine server-side, so the same position and the
 * same slip produce the same figure in either column — which is the whole reason
 * phase 1's realised savings was re-based as part of IB-925.
 *
 * Modelled columns are ABSENT from the payload — not zero, not null — when the
 * viewed version is not the evolving one, because a delay cannot be modelled
 * against a version nobody can change. They are dropped by inspecting what a row
 * actually carries rather than hidden with CSS.
 *
 * There is deliberately **no "held by floor" column**: the duration floor was
 * dropped from the design, so nothing is ever held by one. Its absence is the
 * reason the integrity warning above this table carries so much weight.
 */

/**
 * The key `PeopleDelayScenarioService::NO_LEVEL_KEY` groups every position without
 * a pay rate under. Matched on rather than on an absent `midpoint`, because
 * `midpoint` is the pay band and is omitted entirely from every row for a user
 * without pay-rate visibility — keying the marker off it would italicise the whole
 * table for that audience.
 */
const NO_LEVEL_KEY = 'no-level';

const COUNT = (value) => formatFigure(value, formatSeparateByThousands, { precision: 3 });
const MONEY = (value) => formatFigure(value, formatToFixedMoneyWithLocale);
/* One decimal place through the shared helper, rather than a bespoke
 * `toLocaleString('en-US')` — an average delay is not money, but the divisor and
 * the rounding still belong in one place. */
const DELAY = (value) => formatFigure(value, formatToFixedLocaleString, { precision: 1 });

/* Share at one decimal place, matching the roll-up's own precision — and, for the
 * reason recorded there, not apportioned across rows to make the footer tidy. The
 * column that must foot exactly is modelled saving, and it does. */
const PERCENT = (value) => formatFigure(value, (v, precision) => Number(v).toFixed(precision), { precision: 1, suffix: '%' });

/**
 * `unit` is the label the delay column names, not a conversion: the server sends
 * `average_effective_delay_in_unit` already converted, for the same reason the
 * panel does not convert either — one implementation of the divisor.
 *
 * `worst_slip_days` deliberately stays in days whatever the unit is. It is a
 * realised slip that has already happened, not the modelled delay the unit
 * selector governs.
 */
const columnsFor = (unit) => [
    { key: 'level', label: () => $t('Level'), width: 'tw-w-56', text: true },
    { key: 'behind', label: () => $t('Behind'), width: 'tw-w-24', format: COUNT },
    { key: 'worst_slip_days', label: () => $t('Worst slip (days)'), width: 'tw-w-32', format: COUNT },
    { key: 'realised_saving', label: () => $t('Realised saving'), width: 'tw-w-36', format: MONEY },
    { key: 'positions_moved', label: () => $t('Positions moved'), width: 'tw-w-32', format: COUNT, modelled: true },
    {
        key: 'average_effective_delay_in_unit',
        label: () => $t('Avg effective delay ({{unit}})', { unit }),
        width: 'tw-w-44',
        format: DELAY,
        modelled: true,
    },
    { key: 'modelled_saving', label: () => $t('Modelled saving'), width: 'tw-w-36', format: MONEY, modelled: true },
    { key: 'share', label: () => $t('Share'), width: 'tw-w-24', format: PERCENT, modelled: true },
];

function isMissing(value) {
    return value === null || value === undefined || value === '';
}

/**
 * Absent values sort LAST in both directions — a level with nothing behind
 * schedule has no worst slip, and letting the raw comparison place those blanks
 * interleaves them through the middle of the column on one of the two clicks.
 */
function compareRows(a, b, column, dir) {
    const left = a[column.key];
    const right = b[column.key];

    if (isMissing(left) || isMissing(right)) {
        if (isMissing(left) && isMissing(right)) {
            return 0;
        }

        return isMissing(left) ? 1 : -1;
    }

    const result = typeof left === 'number' && typeof right === 'number' ? left - right : String(left).localeCompare(String(right));

    return dir === SORT_DIR_DESC ? -result : result;
}

function Cell({ column, row }) {
    const value = row[column.key];

    if (isMissing(value)) {
        return <td className={classNames({ 'inbud-numeric-cell': !column.text })}></td>;
    }

    return <td className={classNames({ 'inbud-numeric-cell': !column.text })}>{column.format ? column.format(value) : value}</td>;
}

/**
 * The footer sums the visible rows.
 *
 * Summed here rather than taken from a server-side totals row on purpose: this
 * table's job is to demonstrate that the rows foot to the scenario total shown
 * above it, and a footer computed from a second source could agree with neither.
 */
function totalsRow(rows, columns) {
    const totals = {};

    columns.forEach((column) => {
        if (column.key === 'level') {
            return;
        }

        const values = rows.map((row) => row[column.key]).filter((value) => !isMissing(value));

        if (values.length === 0) {
            totals[column.key] = null;

            return;
        }

        // Worst slip is the worst of them, not their sum; an average delay across
        // levels is not the sum of averages either, and weighting it by positions
        // moved is the only honest total.
        if (column.key === 'worst_slip_days') {
            totals[column.key] = Math.max(...values);
        } else if (column.key === 'average_effective_delay_in_unit') {
            const moved = rows.reduce((carry, row) => carry + (row.positions_moved || 0), 0);
            totals[column.key] =
                moved > 0 ? rows.reduce((carry, row) => carry + (row.average_effective_delay_in_unit || 0) * (row.positions_moved || 0), 0) / moved : null;
        } else if (column.key === 'share') {
            // Stated as a whole rather than left for the reader to add up — see
            // PERCENT above on why the rows themselves need not sum to exactly 100.
            totals[column.key] = values.reduce((carry, value) => carry + value, 0) > 0 ? 100 : 0;
        } else {
            totals[column.key] = values.reduce((carry, value) => carry + value, 0);
        }
    });

    return totals;
}

/**
 * `labelHeader` and `emptyMessage` let the event-level by-department grouping,
 * whose rows share this shape, render through the same table.
 */
export default function PeopleSavingsByLevel({ rows = [], unit, labelHeader, emptyMessage }) {
    const { toggleSort, isAsc, isDesc, type, dir } = useSort({ defaultDir: SORT_DIR_ASC });

    const allColumns = useMemo(() => columnsFor(unit), [unit]);

    // `key in row`, not truthiness: a genuine zero saving keeps its column, while
    // a column the payload omits off Latest Estimate is dropped entirely.
    const columns = useMemo(() => allColumns.filter((column) => !column.modelled || (rows.length > 0 && column.key in rows[0])), [allColumns, rows]);

    const sortedRows = useMemo(() => {
        const column = allColumns.find((candidate) => candidate.key === type);

        return column ? [...rows].sort((a, b) => compareRows(a, b, column, dir)) : rows;
    }, [allColumns, rows, type, dir]);

    if (rows.length === 0) {
        return <p className="tw-text-sm tw-text-charcoal-50 tw-mb-0">{emptyMessage ?? $t('No positions to break down by level yet.')}</p>;
    }

    const totals = totalsRow(rows, columns);

    return (
        <div className="inbud-table_wrapper">
            <table className="inbud-table inbud-table_first-sticky tw-table-fixed">
                <thead>
                    <tr>
                        {columns.map((column) => (
                            <th key={column.key} className={classNames(column.width, 'tw-cursor-pointer')} onClick={() => toggleSort(column.key)}>
                                <TableSortHeaderCell asc={isAsc(column.key)} desc={isDesc(column.key)}>
                                    {column.key === 'level' && labelHeader ? labelHeader : column.label()}
                                </TableSortHeaderCell>
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {sortedRows.map((row) => (
                        <tr key={row.key}>
                            <td>
                                {/* Italic marks the cohort that has no level at all, the same
                                    way the roll-up marks a department's own positions.
                                    Keyed off the row's own identity rather than an absent
                                    midpoint — see NO_LEVEL_KEY above. */}
                                <div className={classNames('tw-truncate', { 'tw-italic': row.key === NO_LEVEL_KEY })}>{row.level}</div>
                            </td>
                            {columns.slice(1).map((column) => (
                                <Cell key={column.key} column={column} row={row} />
                            ))}
                        </tr>
                    ))}
                </tbody>
                <tfoot>
                    <tr className="tw-font-semibold">
                        <td>{$t('Totals')}</td>
                        {columns.slice(1).map((column) => (
                            <Cell key={column.key} column={column} row={totals} />
                        ))}
                    </tr>
                </tfoot>
            </table>
        </div>
    );
}
